import * as Sentry from '@sentry/nextjs';
import {NextWebVitalsMetric} from 'next/app';

// Web Vitals reporting for performance monitoring
// @sentry/nextjs automatically captures Web Vitals, but we add custom performance budget alerting
export function alertOnPerformanceBudgetViolation(metric: NextWebVitalsMetric) {
  // Console log for development debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${metric.name}: ${metric.value}${metric.name === 'CLS' ? '' : 'ms'}`);
  }

  // Custom performance budget alerting - alert if metrics exceed thresholds
  // @sentry/nextjs automatically tracks the Web Vitals themselves
  const defaultBudgets = {
    LCP: 2500, // ms - Largest Contentful Paint (largest element load time)
    FID: 100, // ms - First Input Delay (user interaction response time)
    CLS: 0.1, // score - Cumulative Layout Shift (visual stability, lower is better)
    FCP: 1800, // ms - First Contentful Paint (first content appears)
    TTFB: 800, // ms - Time to First Byte (server response time)
  };

  // Parse performance budgets from environment variable with fallback to defaults
  const getPerformanceBudgets = () => {
    const envBudgets = process.env.NEXT_PUBLIC_PERFORMANCE_BUDGETS;
    if (!envBudgets) {
      return defaultBudgets;
    }

    try {
      const parsed = JSON.parse(envBudgets);
      // Merge with defaults to ensure all required keys exist
      return {
        ...defaultBudgets,
        ...parsed,
      };
    } catch (error) {
      console.warn(
        '[Performance Budgets] Failed to parse NEXT_PUBLIC_PERFORMANCE_BUDGETS, using defaults:',
        error,
      );
      return defaultBudgets;
    }
  };

  const PERFORMANCE_BUDGETS = getPerformanceBudgets();

  const threshold = PERFORMANCE_BUDGETS[metric.name as keyof typeof PERFORMANCE_BUDGETS];
  if (threshold && metric.value > threshold) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[Performance Budget] ${metric.name} ${metric.value}${
          metric.name === 'CLS' ? '' : 'ms'
        } exceeds ${threshold}${metric.name === 'CLS' ? '' : 'ms'} threshold`,
      );
    }
    Sentry.captureMessage(
      `Performance Budget Violated: ${metric.name} (${metric.value}) exceeded threshold (${threshold})`,
      'warning',
    );
  }
}
